import type { BasicEvaluationTemplateDto, EvaluationTemplateConfigurationDto, EvaluationTemplateConfigurationUpdateDto, EvaluationTemplateConfigurationUpdateOutputDto, EvaluationTemplateCopyInputDto, EvaluationTemplateCreateOutputDto, EvaluationTemplateDto, EvaluationTemplateInputDto, EvaluationTemplatePublishInputDto, EvaluationTemplateQuestionGroupDto, EvaluationTemplateQuestionGroupInputDto, EvaluationTemplateSaveInputDto, PagedRequestEvaluationTemplateDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { PagedResultDto } from '../pagination/models';
import type { QuestionConfigurationItemDto, QuestionLastConfigurationOutputDto, QuestionToTemplateInputDto } from '../questions/models';

@Injectable({
  providedIn: 'root',
})
export class EvaluationTemplateService {
  apiName = 'Default';
  

  addEvaluationTemplateQuestionGroupByInputDto = (inputDto: EvaluationTemplateQuestionGroupInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EvaluationTemplateQuestionGroupDto>({
      method: 'POST',
      url: '/api/app/evaluation-template/evaluation-template-question-group',
      body: inputDto,
    },
    { apiName: this.apiName,...config });
  

  addNewQuestionsToTemplateByQuestionsToAddToTemplate = (questionsToAddToTemplate: QuestionToTemplateInputDto[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, QuestionLastConfigurationOutputDto>({
      method: 'POST',
      url: '/api/app/evaluation-template/new-questions-to-template',
      body: questionsToAddToTemplate,
    },
    { apiName: this.apiName,...config });
  

  copyTemplateByEvaluationTemplateCopyInput = (evaluationTemplateCopyInput: EvaluationTemplateCopyInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'POST',
      url: '/api/app/evaluation-template/copy-template',
      body: evaluationTemplateCopyInput,
    },
    { apiName: this.apiName,...config });
  

  createEvaluationTemplateByInputDto = (inputDto: EvaluationTemplateInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EvaluationTemplateCreateOutputDto>({
      method: 'POST',
      url: '/api/app/evaluation-template/evaluation-template',
      body: inputDto,
    },
    { apiName: this.apiName,...config });
  

  deleteEvaluationTemplateByEvaluationTemplateId = (evaluationTemplateId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/evaluation-template/evaluation-template/${evaluationTemplateId}`,
    },
    { apiName: this.apiName,...config });
  

  deleteEvaluationTemplateQuestionGroupByEvaluationTemplateIdAndQuestionGroupId = (evaluationTemplateId: number, questionGroupId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/app/evaluation-template/evaluation-template-question-group',
      params: { evaluationTemplateId, questionGroupId },
    },
    { apiName: this.apiName,...config });
  

  deleteQuestionConfigurationItemFromTemplateByQuestionConfigurationItemIdAndEvaluationTemplateQuestionGroupId = (questionConfigurationItemId: number, evaluationTemplateQuestionGroupId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, QuestionConfigurationItemDto[]>({
      method: 'DELETE',
      url: '/api/app/evaluation-template/question-configuration-item-from-template',
      params: { questionConfigurationItemId, evaluationTemplateQuestionGroupId },
    },
    { apiName: this.apiName,...config });
  

  editQuestionConfigurationItemByEvaluationTemplateConfigurationUpdate = (evaluationTemplateConfigurationUpdate: EvaluationTemplateConfigurationUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/evaluation-template/edit-question-configuration-item',
      body: evaluationTemplateConfigurationUpdate,
    },
    { apiName: this.apiName,...config });
  

  getAllBasicTemplatesByInput = (input: PagedRequestEvaluationTemplateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, BasicEvaluationTemplateDto[]>({
      method: 'GET',
      url: '/api/app/evaluation-template/basic-templates',
      params: { nameFilter: input.nameFilter, state: input.state, areaIds: input.areaIds, subAreaIds: input.subAreaIds, filterByRoles: input.filterByRoles, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getAllTemplatesByInput = (input: PagedRequestEvaluationTemplateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<EvaluationTemplateDto>>({
      method: 'GET',
      url: '/api/app/evaluation-template/templates',
      params: { nameFilter: input.nameFilter, state: input.state, areaIds: input.areaIds, subAreaIds: input.subAreaIds, filterByRoles: input.filterByRoles, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getEvaluationTemplateConfigurationDtoByEvaluationTemplateId = (evaluationTemplateId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EvaluationTemplateConfigurationDto>({
      method: 'GET',
      url: `/api/app/evaluation-template/evaluation-template-configuration-dto/${evaluationTemplateId}`,
    },
    { apiName: this.apiName,...config });
  

  getEvaluationTemplateQuestionGroupsByEvaluationTemplateId = (evaluationTemplateId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EvaluationTemplateQuestionGroupDto[]>({
      method: 'GET',
      url: `/api/app/evaluation-template/evaluation-template-question-groups/${evaluationTemplateId}`,
    },
    { apiName: this.apiName,...config });
  

  getQuestionDataForUpdateByQuestionConfigurationItemId = (questionConfigurationItemId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, EvaluationTemplateConfigurationUpdateOutputDto>({
      method: 'GET',
      url: `/api/app/evaluation-template/question-data-for-update/${questionConfigurationItemId}`,
    },
    { apiName: this.apiName,...config });
  

  publishTemplateByEvaluationTemplate = (evaluationTemplate: EvaluationTemplatePublishInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/evaluation-template/publish-template',
      body: evaluationTemplate,
    },
    { apiName: this.apiName,...config });
  

  reorderQuestionConfigurationItemsByEvaluationTemplateQuestionGroupIdAndQuestionConfigurationItemDtos = (evaluationTemplateQuestionGroupId: number, questionConfigurationItemDtos: QuestionConfigurationItemDto[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, QuestionConfigurationItemDto[]>({
      method: 'POST',
      url: `/api/app/evaluation-template/reorder-question-configuration-items/${evaluationTemplateQuestionGroupId}`,
      body: questionConfigurationItemDtos,
    },
    { apiName: this.apiName,...config });
  

  saveTemplateByEvaluationTemplateSaveInput = (evaluationTemplateSaveInput: EvaluationTemplateSaveInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/evaluation-template/save-template',
      body: evaluationTemplateSaveInput,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
